import { css } from '@emotion/react'
import { Button, Cell, FormControl, Grid, HFlow, Icon, Tooltip, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import {
  CheckboxField,
  createDecorator,
  Form,
  FormRenderProps,
  SelectField,
  SubmitButton,
  SubmitlessForm,
} from 'components/form'
import { DateRangeField } from 'components/form/field/daterange/DateRangeField'
import { TipoEncaminhamentoSelectField } from 'components/form/field/select/TipoEncaminhamentoSelectField'
import { PopperButton, PopperControls } from 'components/popper'
import { useFlags } from 'config/useFlagsContext'
import { FormState } from 'final-form'
import { TipoEncaminhamentoExternoEnum } from 'graphql/types.generated'
import { useFilterCounter } from 'hooks/useFilterCounter'
import { FormSpy } from 'react-final-form'
import { metaPath } from 'util/metaPath'

import { EncaminhamentoExternoFilterModel, EspecialidadeSisregModel } from '../model-encaminhamentoExterno'

interface EncaminhamentoExternoFilterProps {
  filter: EncaminhamentoExternoFilterModel
  dataReferencia: Instant
  onChange(values: EncaminhamentoExternoFilterModel): void
  especialidadeSelectFieldValues: { id: ID; descricao: string }[]
  isAtendimentoOdonto?: boolean
}

const name = metaPath<EncaminhamentoExternoFilterModel>()

const itemToString = (item: EspecialidadeSisregModel) => item?.descricao?.capitalize()

export const EncaminhamentoExternoFilter = ({
  filter,
  onChange,
  dataReferencia,
  especialidadeSelectFieldValues,
  isAtendimentoOdonto,
}: EncaminhamentoExternoFilterProps) => {
  const { PILOTO_VINCULACAO_CUIDADO_ENABLED } = useFlags()

  const activeFiltersCount = useFilterCounter(filter, {
    periodo: true,
    somenteMeus: true,
  })

  const classes = createStyles()

  const handleChange = ({ values }: FormState<EncaminhamentoExternoFilterModel>) => {
    onChange({
      ...filter,
      tipoEncaminhamento: values.tipoEncaminhamento,
      especialidade: values.especialidade,
      periodo: values.periodo,
      somenteMeus: values.somenteMeus,
    })
  }

  const handlePopperSubmit = (model: EncaminhamentoExternoFilterModel) => {
    onChange({ ...filter, somenteMeus: model.somenteMeus, periodo: model.periodo })
  }

  const renderPopper = ({ close }: PopperControls) => {
    const submitAndClose = (values: EncaminhamentoExternoFilterModel) => {
      handlePopperSubmit(values)
      close()
    }

    return (
      <Form<EncaminhamentoExternoFilterModel>
        initialValues={filter}
        onSubmit={submitAndClose}
        render={(formProps: FormRenderProps<EncaminhamentoExternoFilterModel>) => (
          <Box style={classes.extraFilterBox}>
            <Grid>
              <Cell size={12}>
                <CheckboxField name={name.somenteMeus} label='Ver somente os meus encaminhamentos' />
              </Cell>
              <Cell size={12}>
                <DateRangeField name={name.periodo} label='Período' maxDate={new Date(dataReferencia)} />
              </Cell>
              <Cell size={12} alignSelf='flex-end'>
                <HFlow justifyContent='flex-end'>
                  <Button size='small' onClick={close}>
                    Fechar
                  </Button>
                  <SubmitButton size='small' handleSubmit={formProps.handleSubmit}>
                    Filtrar
                  </SubmitButton>
                </HFlow>
              </Cell>
            </Grid>
          </Box>
        )}
      />
    )
  }

  const renderForm = ({ values }: FormRenderProps<EncaminhamentoExternoFilterModel>) => (
    <VFlow vSpacing={PILOTO_VINCULACAO_CUIDADO_ENABLED && !isAtendimentoOdonto ? 0.5 : 0}>
      <FormSpy onChange={handleChange} />
      {(!PILOTO_VINCULACAO_CUIDADO_ENABLED || isAtendimentoOdonto) && (
        <HFlow alignItems='center' justifyContent='space-between'>
          <HFlow>
            <FormControl>
              <TipoEncaminhamentoSelectField
                name={name.tipoEncaminhamento}
                disabled={!!values.especialidade}
                placeholder='Tipo de encaminhamento'
              />
            </FormControl>
            <FormControl>
              <Tooltip
                text={
                  !!values.tipoEncaminhamento &&
                  values.tipoEncaminhamento !== TipoEncaminhamentoExternoEnum.SERVICO_ESPECIALIZADO &&
                  'Necessário que o tipo de encaminhamento seja serviço especializado'
                }
              >
                <SelectField<EspecialidadeSisregModel>
                  name={name.especialidade}
                  items={especialidadeSelectFieldValues}
                  itemToString={itemToString}
                  disabled={
                    !!values.tipoEncaminhamento &&
                    values.tipoEncaminhamento !== TipoEncaminhamentoExternoEnum.SERVICO_ESPECIALIZADO
                  }
                  placeholder='Especialidade'
                />
              </Tooltip>
            </FormControl>
          </HFlow>
          <PopperButton kind='primary' size='small' placement='bottom-end' renderPopper={renderPopper}>
            <Icon icon='filterOutline' />
            Filtros{activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ''}
          </PopperButton>
        </HFlow>
      )}

      {PILOTO_VINCULACAO_CUIDADO_ENABLED && !isAtendimentoOdonto && (
        <div
          css={css`
            margin-right: 87px;
          `}
        >
          <Grid gap={1} gapVertical={0.5}>
            <Cell size={6} alignSelf='flex-start'>
              <TipoEncaminhamentoSelectField
                name={name.tipoEncaminhamento}
                label='Tipo de encaminhamento'
                disabled={!!values.especialidade}
                placeholder='Tipo de encaminhamento'
              />
            </Cell>

            <Cell size={6} alignSelf='flex-start'>
              <Tooltip
                text={
                  !!values.tipoEncaminhamento &&
                  values.tipoEncaminhamento !== TipoEncaminhamentoExternoEnum.SERVICO_ESPECIALIZADO &&
                  'Necessário que o tipo de encaminhamento seja serviço especializado'
                }
              >
                <SelectField<EspecialidadeSisregModel>
                  name={name.especialidade}
                  label='Especialidade'
                  items={especialidadeSelectFieldValues}
                  itemToString={itemToString}
                  disabled={
                    !!values.tipoEncaminhamento &&
                    values.tipoEncaminhamento !== TipoEncaminhamentoExternoEnum.SERVICO_ESPECIALIZADO
                  }
                  placeholder='Especialidade'
                />
              </Tooltip>
            </Cell>

            <Cell size={6} alignSelf='flex-start'>
              <DateRangeField name={name.periodo} label='Período' maxDate={new Date(dataReferencia)} />
            </Cell>

            <Cell
              size={6}
              alignSelf='flex-start'
              css={css`
                margin-top: 2rem;
                margin-left: 0.2rem;
              `}
            >
              <CheckboxField name={name.somenteMeus} label='Ver somente os meus encaminhamentos' />
            </Cell>
          </Grid>
        </div>
      )}
    </VFlow>
  )

  return <SubmitlessForm<EncaminhamentoExternoFilterModel> render={renderForm} decorators={filtersDecorators} />
}

const createStyles = () => ({
  extraFilterBox: css`
    width: 25rem;
  `,
})

const filtersDecorators = [
  createDecorator({
    field: name.especialidade.absolutePath(),
    updates: {
      [name.tipoEncaminhamento.absolutePath()]: (value: EspecialidadeSisregModel) => {
        if (value) {
          return TipoEncaminhamentoExternoEnum.SERVICO_ESPECIALIZADO
        }
      },
    },
  }),
]
